<!-- The box. Borderless field inside a bordered row so the default variant's icon reads as part of
     the field, and two lines tall before it grows - a comment with a couple of mentions in it runs
     onto a second line constantly, and a box that jumps taller on every other sentence is what
     reads as unstable. The third line and everything after it does grow the box. -->
<div #box
     class="w-full min-w-0 flex flex-wrap content-center items-start gap-3 px-4 py-0.5 rounded-lg border"
     [style.minHeight.px]="minHeightPx()"
     [ngClass]="[fillClass(), borderClass()]">
  @if (showIcon()) {
    <i class="fa-regular fa-comment text-surface-400 shrink-0 leading-[18px] pt-0.5"></i>
  }
  <!-- Relative only to hang the placeholder off: a contenteditable box has none of its own. -->
  <div class="relative flex-1 min-w-0">
    <!-- contenteditable, not a textarea: a mention is drawn as the card a posted comment shows it
         as, and only an element tree can carry one. whitespace-pre-wrap so a newline is a newline
         in the text nodes themselves, which is what lets an offset into the display text be
         followed straight into the DOM. Grows with its content up to max-h-40; no height to set,
         where a textarea had to be measured. -->
    <div #field
         [attr.data-testid]="testId()"
         class="w-full min-w-0 max-h-40 overflow-y-auto outline-none whitespace-pre-wrap break-words text-xs font-normal font-['Inter'] leading-[18px]"
         role="textbox"
         aria-multiline="true"
         [ngClass]="disabled() ? 'text-[var(--p-form-field-disabled-color)]' : 'text-surface-700 cursor-text'"
         [attr.contenteditable]="disabled() ? 'false' : 'true'"
         [attr.aria-disabled]="disabled() ? 'true' : null"
         [attr.aria-label]="placeholder() || null"
         (input)="onInput()"
         (paste)="onPaste($event)"
         (keydown)="onKeydown($event)"
         (focus)="focusChange.emit(true)"
         (blur)="onBlur()"></div>
    <!-- truncate: the prompt is one line cut short in a narrow box rather than wrapping to two.
         It sits over the field, so it is centred with it while the box is empty and lands exactly
         where the first character typed will. -->
    @if (placeholder() && !displayText()) {
      <span class="pointer-events-none absolute inset-0 truncate text-xs font-normal font-['Inter'] leading-[18px]"
            [ngClass]="disabled() ? 'text-[var(--p-form-field-disabled-color)]' : 'text-surface-400'">{{ placeholder() }}</span>
    }
  </div>
</div>

<!-- Fixed, not absolute: both hosts sit inside an overflow-hidden panel or a scrolling chain, and
     an absolute list would be clipped by whichever one it is in. Measured off the box above. -->
@if (pickerOpen()) {
  <div class="fixed z-[60] p-4 bg-white rounded-2xl border border-surface-200 shadow-lg"
       data-testid="ta-comment-mention-picker"
       [style.left.px]="pickerLeft()"
       [style.width.px]="pickerWidth()"
       [style.top.px]="pickerTop()"
       [style.bottom.px]="pickerBottom()">
    <div class="text-surface-900 text-xs font-semibold font-['Inter'] mb-3">People</div>
    <div class="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
      <!-- The list is fetched when the box is focused, so an @ typed straight away can arrive
           first. Says so rather than showing an empty card. -->
      @if (peopleLoading() && suggestions().length === 0) {
        <div data-testid="ta-comment-mention-loading"
             class="flex items-center gap-2 text-surface-500 text-xs font-normal font-['Inter'] italic">
          <i class="fa-solid fa-spinner fa-spin"></i>Loading people...
        </div>
      }

      @for (person of suggestions(); track person.id; let index = $index) {
        <!-- preventDefault on mousedown keeps focus in the field across the click: without it the
             field blurs first, which closes this list before the click lands. -->
        <button type="button"
                class="w-full flex items-center gap-3 px-2 py-1.5 rounded-full border text-left cursor-pointer"
                data-testid="ta-comment-mention-option"
                [ngClass]="index === activeIndex()
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-surface-200 hover:bg-surface-50'"
                (mousedown)="$event.preventDefault()"
                (click)="accept(person)">
          <span class="shrink-0 w-8 h-8 rounded-full bg-slate-400 flex items-center justify-center text-white text-xs font-semibold font-['Inter'] select-none">
            {{ mentionInitials(person) }}
          </span>
          <span class="min-w-0 flex items-baseline gap-2">
            <span class="text-surface-700 text-xs font-semibold font-['Inter'] whitespace-nowrap">{{ person.name }}</span>
            @if (person.email) {
              <span class="text-surface-700 text-xs font-normal font-['Inter']">-</span>
              <span class="min-w-0 truncate text-surface-700 text-[0.625rem] font-normal font-['Inter']">{{ person.email }}</span>
            }
          </span>
        </button>
      }
    </div>
  </div>
}
